import { logger } from '@shared-services/log-service';
import { logBookingInfo } from '../loggingUtils';
import { CalEventData } from '../types';
import { HttpError } from './errors';
import {
    transformRegularBooking,
    transformSeatReferenceBooking,
} from './transformers';
import { FetchOptions } from './types';

class CalApi {
    static CAL_API_VERSION = '2024-08-13';

    private calApiDomain: string = 'api.cal.com';
    private calApiBaseUrl?: string;
    private calClientId: string = '';

    get apiUrl() {
        if (this.calApiBaseUrl) {
            return `${this.calApiBaseUrl.replace(/\/+$/, '')}/v2`;
        }
        return `https://${this.calApiDomain}/v2`;
    }

    public setCalDomain(domain: string = 'api.cal.com') {
        this.calApiDomain = domain;
    }

    /**
     * Engine-resolved base URL (ms-booking's calApiBaseUrl) — preferred over
     * calApiDomain when present, so native-engine sites route there instead
     * of always hitting cal.com. Falls back to calApiDomain when absent/empty.
     */
    public setApiBaseUrl(baseUrl?: string) {
        this.calApiBaseUrl = baseUrl || undefined;
    }

    public setCalClientId(clientId: string) {
        this.calClientId = clientId;
    }

    async fetchCalCom(url: string, options: FetchOptions = {}) {
        const requestUrl = calApi.apiUrl + url;
        const res = await fetch(requestUrl, options);

        if (!res.ok) {
            throw new HttpError({
                requestUrl,
                requestOptions: options,
                responseStatus: res.status,
                responseBody: await this.getErrorResponseBody(res),
            });
        }

        const jsonRes = await res.json();
        return jsonRes.data || jsonRes;
    }

    private async getErrorResponseBody(response: Response): Promise<unknown> {
        try {
            return await response.json();
        } catch {
            return await response.text();
        }
    }

    async cancelBooking({
        bookingUid,
        cancellationReason,
        seatUid,
    }: {
        bookingUid: string;
        cancellationReason?: string;
        seatUid?: string;
    }) {
        const url = `/bookings/${bookingUid}/cancel`;
        try {
            const options = {
                method: 'POST',
                headers: {
                    'cal-api-version': CalApi.CAL_API_VERSION,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    cancellationReason,
                    ...(seatUid ? { seatUid } : undefined),
                }),
            };
            const res = await this.fetchCalCom(url, options);
            if (res.error) {
                throw new Error(res.error?.message || res.error);
            }
            return res;
        } catch (e) {
            logger.error({
                message: `Failed to cancel booking with UID: ${bookingUid}`,
                errorMessage: e?.toString(),
                url,
                tags: ['cal-booking', 'cancel-booking'],
            });
            throw e;
        }
    }

    async getBooking(bookingUid: string) {
        const url = `/bookings/${bookingUid}`;
        const result = await this.fetchCalCom(url, {
            method: 'GET',
        });

        // need to unify the result to the Booking type
        return transformRegularBooking(result);
    }

    async getBookingBySeatUid(seatUid: string) {
        const url = `/bookings/by-seat/${seatUid}`;
        const result = await this.fetchCalCom(url, {
            method: 'GET',
            headers: {
                'x-cal-client-id': this.calClientId,
                'cal-api-version': CalApi.CAL_API_VERSION,
                'Content-Type': 'application/json',
            },
        });

        // need to unify the result to the Booking type
        return transformSeatReferenceBooking(result);
    }

    async getBookingByUidOrSeatUid(uidOrSeatUid: string) {
        logBookingInfo(`Getting booking data ${uidOrSeatUid}`);
        return calApi.getBooking(uidOrSeatUid).catch((error) => {
            if (HttpError.isNotFoundError(error)) {
                logBookingInfo(
                    `Attempting to get booking data by seat UID ${uidOrSeatUid}`
                );
                return calApi.getBookingBySeatUid(uidOrSeatUid);
            }
            throw error;
        });
    }

    async getTeamEvents({
        organizationId,
        teamId,
    }: {
        organizationId: number;
        teamId: number;
    }): Promise<CalEventData[]> {
        const url = `/organizations/${organizationId}/teams/${teamId}/event-types`;
        return await this.fetchCalCom(url, {
            method: 'GET',
            headers: {
                'x-cal-client-id': this.calClientId,
                'cal-api-version': CalApi.CAL_API_VERSION,
                'Content-Type': 'application/json',
            },
        });
    }

    async getTeamEventType({
        eventSlug,
        orgId,
        teamId,
    }: {
        eventSlug: string;
        orgId: number;
        teamId: number;
    }) {
        const url = `/atoms/event-types/${eventSlug}/public?isTeamEvent=true&teamId=${teamId}&username=&orgId=${orgId}`;
        return await this.fetchCalCom(url, {
            method: 'GET',
            headers: {
                'x-cal-client-id': this.calClientId,
                'cal-api-version': CalApi.CAL_API_VERSION,
                'Content-Type': 'application/json',
            },
        });
    }
}

const calApi = new CalApi();

export default calApi;
